'use client';

import { type ReactNode, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import FaIcon, { faXmark } from '../../icon/FaIcon/FaIcon';
import styles from './ModalBase.module.scss';

// 同時に開いているモーダルの数と、最初の1枚が開く前の body の overflow。
// 詳細モーダルの上に拡大モーダルを重ねるケースがあるため、最後の1枚が閉じたときだけ元の値へ戻す。
let openModalCount = 0;
let overflowBeforeFirstModal = '';

type Props = {
  isOpen: boolean;
  onClose: VoidFunction;
  children?: ReactNode;
  textColor?: string;
  bgColor?: 'white' | 'gray100' | 'gray200' | 'transparent';
  showDefaultCloseButton?: boolean;
  showDefaultCloseButtonOnlyOnPc?: boolean;
  showBottomCloseButton?: boolean;
  maskBgColor?: string;
  closeButtonBgColor?: string;
  modalContainerSize?: 'default' | 'xSmall' | 'small' | 'mediumLarge' | 'large' | 'xLarge';
  isBackgroundNoScroll?: boolean;
  closeOnBackgroundClick?: boolean;
  /**
   * PC での縦位置。既定は中央寄せ。'top' にすると上寄せになり、コンテンツ高が変わっても上端が固定される。
   * 高さが可変（送りでアイテム数が変わる等）で中央寄せだと上下に飛ぶモーダル向け。SP は常に上寄せ。
   */
  pcVerticalAlign?: 'center' | 'top';
};

function ModalBase({
  isOpen,
  onClose,
  children,
  textColor = 'black',
  bgColor = 'white',
  showDefaultCloseButton = true,
  showDefaultCloseButtonOnlyOnPc = false,
  showBottomCloseButton = false,
  maskBgColor = 'black',
  closeButtonBgColor = 'gray100',
  modalContainerSize = 'default',
  closeOnBackgroundClick = false,
  pcVerticalAlign = 'center',
}: Props) {
  // モーダル開いてる時は背景スクロールさせない。元の overflow を退避し、クリーンアップで復元する
  // （'auto' 固定で戻すと、モーダル外で overflow を個別指定していた場合にその値を壊すため）
  useEffect(() => {
    if (!isOpen) return;
    // 退避は最初の1枚だけ。2枚目以降が退避すると1枚目が設定した 'hidden' を掴み、元の値に戻せなくなる
    if (openModalCount === 0) {
      overflowBeforeFirstModal = document.body.style.overflow;
    }
    openModalCount += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      openModalCount -= 1;
      // 重なっている下のモーダルが開いている間は戻さない（閉じる順序に依存せず判定するため枚数で見る）
      if (openModalCount === 0) {
        document.body.style.overflow = overflowBeforeFirstModal;
      }
    };
  }, [isOpen]);

  const generateStyle = ({ textColor, bgColor }: Pick<Props, 'textColor' | 'bgColor'>): React.CSSProperties => {
    if (bgColor === 'transparent') {
      return {
        backgroundColor: 'transparent',
      };
    }
    return {
      backgroundColor: `var(--color-${bgColor})`,
      color: `var(--color-${textColor})`,
    };
  };

  const generateMaskStyle = ({ maskBgColor }: Pick<Props, 'maskBgColor'>): React.CSSProperties => {
    return {
      backgroundColor: maskBgColor,
    };
  };

  const generateCloseButtonStyle = ({ closeButtonBgColor }: Pick<Props, 'closeButtonBgColor'>): React.CSSProperties => {
    if (closeButtonBgColor === 'gray100') {
      return {
        backgroundColor: `var(--color-${closeButtonBgColor})`,
        padding: '0px',
      };
    }
    return {
      backgroundColor: closeButtonBgColor,
    };
  };

  // warning対策
  const nodeRef = useRef(null);

  // 背景クリック時のハンドラ
  const handleBackgroundClick = () => {
    if (closeOnBackgroundClick) {
      onClose();
    }
  };

  // モーダルコンテナ内のクリックで閉じないようにする
  const handleModalContainerClick = (e: React.MouseEvent<HTMLElement>) => {
    // 透過背景で背景クリックで閉じる設定の場合、イベント伝播を止めない
    if (bgColor === 'transparent' && closeOnBackgroundClick) {
      return;
    }
    e.stopPropagation();
  };

  return (
    <CSSTransition in={isOpen} timeout={300} unmountOnExit classNames={styles} nodeRef={nodeRef}>
      <div ref={nodeRef} className={styles.modalWrapper}>
        <div className={styles.modalMask} style={generateMaskStyle({ maskBgColor })} />
        <section
          className={`${styles.fixedModalContainer} ${pcVerticalAlign === 'top' ? styles.pcAlignTop : styles.pcAlignCenter}`}
          onClick={handleBackgroundClick}
        >
          <div className={`${styles.modalContainer} ${styles[`${modalContainerSize}ModalContainer`]}`}>
            <div
              className={`${styles.modalBody} ${showBottomCloseButton ? styles.defaultHeightModalBody : styles.largeHeightModalBody}`}
              style={generateStyle({ textColor, bgColor })}
              onClick={handleModalContainerClick}
            >
              {showDefaultCloseButton && (
                <div className={styles.modalHeader}>
                  <button
                    type='button'
                    className={`${styles.modalClose} ${showDefaultCloseButtonOnlyOnPc ? styles.hideOnSp : ''}`}
                    onClick={() => onClose()}
                    style={generateCloseButtonStyle({ closeButtonBgColor })}
                  >
                    <FaIcon icon={faXmark} />
                  </button>
                </div>
              )}
              {children}
            </div>
            {showBottomCloseButton && (
              <button type='button' className={styles.modalFooterClose} onClick={() => onClose()}>
                閉じる
              </button>
            )}
          </div>
        </section>
      </div>
    </CSSTransition>
  );
}

export default ModalBase;
